import { supabase } from "@/lib/supabase";
import { getAdminClient } from "@/lib/supabaseAdmin";

const TABLE_NAME = "workspaces";

/**
 * Creates a new workspace
 * @param {Object} workspace - The workspace data
 * @returns {Promise} - The created workspace
 */
export const createWorkspace = async ({
  name,
  description = "",
  isPersonal = false,
  isTeam = false,
  teamKey = null,
  userId,
  ownerId = userId,
  instructions = "",
  includeProfile = false,
  includeInstructions = false,
  imagePath = null,
  avatarUrl = null,
  theme = "system",
  textsize = "medium",
  codetheme = "github",
}) => {
  // Generate a team key if this is a team workspace and no key is provided
  if (isTeam && !teamKey) {
    teamKey = generateTeamKey();
  }

  // Using a transaction to ensure both workspace and member are created
  const { data: workspace, error: workspaceError } = await supabase
    .from("workspaces")
    .insert({
      name,
      description,
      is_personal: isPersonal,
      is_team: isTeam,
      team_key: teamKey,

      user_id: userId,
      owner_id: ownerId,
      instructions,
      include_profile_context: includeProfile,
      include_workspace_instructions: includeInstructions,
      image_path: imagePath,
      avatar_url: avatarUrl, // Make sure we pass avatarUrl too
      theme,
      textsize,
      codetheme,
      workspace_threads: [],
      workspace_assistants: [],
      workspace_members: [userId], // Add creator to workspace members array
      created_now: true, // Explicitly set created_now flag
      editor_file_data: {},
      file_data: {
        file_types: {},
        total_size: 0,
        total_files: 0,
        recent_files: [],
      },
      settings: {
        notifications: false,
        private: false,
        allowMemberInvites: isTeam, // Enable member invites for team workspaces
      },
      metadata: {
        tags: [],
        stats: {
          chat_count: 0,
          file_count: 0,
          member_count: 1, // Set initial member count to 1
          assistant_count: 0,
          total_storage: 0,
        },
        starred: false,
        version: "1.0.0",
        archived: false,
        created_by: userId,
        created_now: true, // Also set in metadata for redundancy
        updated_by: userId,
        last_activity: new Date().toISOString(),
        is_team_workspace: isTeam,
        team_created_at: isTeam ? new Date().toISOString() : null,
      },
    })
    .select()
    .single();

  if (workspaceError) throw workspaceError;

  // Log the created workspace

  // Add creator as owner member in workspace_members table
  const { error: memberError } = await supabase
    .from("workspace_members")
    .insert({
      user_id: userId,
      workspace_id: workspace.id,
      role: "owner", // Always set creator as owner
      permissions: {
        workspace: {
          edit: true,
          delete: true,
          invite_members: true,
          manage_roles: true,
        },
        files: {
          upload: true,
          delete: true,
          share: true,
        },
        memories: {
          create: true,
          edit: true,
          delete: true,
        },
        settings: {
          view: true,
          edit: true,
        },
      },
    });

  if (memberError) {
    // If creating the member fails, log error but don't fail the entire operation
    console.error("Error adding member to workspace:", memberError);
  }

  return workspace;
};

/**
 * Generates a unique team key
 * @returns {string} - The generated team key
 */
const generateTeamKey = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 10);
  return `team_${timestamp}_${randomStr}`;
};

/**
 * Gets a workspace by ID
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise} - The workspace data
 */
export const getWorkspace = async (workspaceId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", workspaceId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error getting workspace:", error);
    throw error;
  }
};

/**
 * Gets all workspaces for a user
 * @param {string} userId - The user ID
 * @returns {Promise} - Array of workspaces
 */
export const getUserWorkspaces = async (userId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error getting user workspaces:", error);
    throw error;
  }
};

/**
 * Updates a workspace
 * @param {string} workspaceId - The workspace ID
 * @param {Object} updates - The fields to update
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspace = async (workspaceId, updates) => {
  try {
    // Filter out undefined values
    const validUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined)
    );

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(validUpdates)
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace:", error);
    throw error;
  }
};

/**
 * Deletes a workspace
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise} - The deleted workspace
 */
export const deleteWorkspace = async (workspaceId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .delete()
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error deleting workspace:", error);
    throw error;
  }
};

/**
 * Updates workspace settings
 * @param {string} workspaceId - The workspace ID
 * @param {Object} settings - The new settings object
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceSettings = async (workspaceId, settings) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ settings })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace settings:", error);
    throw error;
  }
};

/**
 * Updates workspace metadata
 * @param {string} workspaceId - The workspace ID
 * @param {Object} metadata - The new metadata object
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceMetadata = async (workspaceId, metadata) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ metadata })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace metadata:", error);
    throw error;
  }
};

/**
 * Gets the home workspace for a user
 * @param {string} userId - The user ID
 * @returns {Promise} - The home workspace data
 */
export const getHomeWorkspace = async (userId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("user_id", userId)
      .eq("is_home", true)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error getting home workspace:", error);
    throw error;
  }
};

/**
 * Updates workspace file data
 * @param {string} workspaceId - The workspace ID
 * @param {Object} fileData - The new file data object
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceFileData = async (workspaceId, fileData) => {
  try {
    // Get current workspace file data
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("file_data")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Merge with existing file data
    const updatedFileData = {
      ...workspace.file_data,
      ...fileData,
      recent_files: [
        ...(fileData.recent_files || []),
        ...(workspace.file_data.recent_files || []).slice(0, 10), // Keep only 10 most recent files
      ],
    };

    // Update workspace
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ file_data: updatedFileData })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace file data:", error);
    throw error;
  }
};

/**
 * Updates workspace vector data
 * @param {string} workspaceId - The workspace ID
 * @param {Object} vectorData - The new vector data object
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceVectorData = async (workspaceId, vectorData) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ vector_data: vectorData })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace vector data:", error);
    throw error;
  }
};

/**
 * Updates workspace stats
 * @param {string} workspaceId - The workspace ID
 * @param {Object} stats - The stats to update
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceStats = async (workspaceId, stats) => {
  try {
    // Get current workspace
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Update stats in metadata
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        ...stats,
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ metadata: updatedMetadata })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace stats:", error);
    throw error;
  }
};

/**
 * Increments a workspace stat
 * @param {string} workspaceId - The workspace ID
 * @param {string} statKey - The stat key to increment
 * @param {number} amount - The amount to increment by
 * @returns {Promise} - The updated workspace
 */
export const incrementWorkspaceStat = async (
  workspaceId,
  statKey,
  amount = 1
) => {
  try {
    // Get current workspace
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Update stat
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        [statKey]: (workspace.metadata.stats[statKey] || 0) + amount,
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ metadata: updatedMetadata })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error incrementing workspace stat:", error);
    throw error;
  }
};

/**
 * Updates workspace storage usage
 * @param {string} workspaceId - The workspace ID
 * @param {number} bytes - The number of bytes to add/subtract
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceStorage = async (workspaceId, bytes) => {
  try {
    // Get current workspace
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Update storage in stats
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        total_storage: Math.max(
          0,
          (workspace.metadata.stats.total_storage || 0) + bytes
        ),
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ metadata: updatedMetadata })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace storage:", error);
    throw error;
  }
};

/**
 * Updates workspace last activity
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceActivity = async (workspaceId) => {
  try {
    // Get current workspace
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Update last activity
    const updatedMetadata = {
      ...workspace.metadata,
      last_activity: new Date().toISOString(),
    };

    // Update workspace
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ metadata: updatedMetadata })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace activity:", error);
    throw error;
  }
};

/**
 * Updates workspace appearance
 * @param {string} workspaceId - The workspace ID
 * @param {Object} appearance - The appearance settings
 * @param {string} appearance.theme - The theme setting
 * @param {string} appearance.textsize - The text size setting
 * @param {string} appearance.codetheme - The code theme setting
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceAppearance = async (
  workspaceId,
  { theme, textsize, codetheme }
) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({ theme, textsize, codetheme })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace appearance:", error);
    throw error;
  }
};

/**
 * Add a thread to a workspace and update metadata
 * IMPORTANT: This function ensures the thread is removed from all other workspaces
 * before adding it to the target workspace to prevent duplicates.
 *
 * @param {string} workspaceId - The workspace ID
 * @param {string} threadId - The thread ID to add
 * @param {boolean} ensureUnique - If true (default), removes thread from all other workspaces first
 * @returns {Promise<object>} - The updated workspace
 */
export async function addThreadToWorkspace(
  workspaceId,
  threadId,
  ensureUnique = true
) {
  try {
    // Ensure IDs are properly formatted
    const safeWorkspaceId = String(workspaceId).replace(/[^\w-]/g, "");
    const safeThreadId = String(threadId).replace(/[^\w-]/g, "");

    // Get admin client to bypass RLS
    const admin = await getAdminClient();

    // CRITICAL: If ensureUnique is true, remove thread from ALL other workspaces first
    // This prevents the thread from existing in multiple workspaces simultaneously
    if (ensureUnique) {
      // Get the thread to find its user_id
      const { data: threadData } = await admin
        .from("threads")
        .select("user_id")
        .eq("id", safeThreadId)
        .single();

      if (threadData?.user_id) {
        // Get all workspaces for this user
        const { data: allUserWorkspaces } = await admin
          .from("workspaces")
          .select("id, workspace_threads")
          .eq("user_id", threadData.user_id);

        if (allUserWorkspaces && allUserWorkspaces.length > 0) {
          // Find workspaces that contain this thread (excluding target workspace)
          const workspacesToClean = allUserWorkspaces.filter(
            (ws) =>
              ws.id !== safeWorkspaceId &&
              (ws.workspace_threads || []).includes(safeThreadId)
          );

          // Remove thread from all other workspaces
          if (workspacesToClean.length > 0) {
            await Promise.all(
              workspacesToClean.map((ws) =>
                removeThreadFromWorkspace(ws.id, safeThreadId).catch(
                  (error) => {
                    console.warn(
                      `Failed to remove thread ${safeThreadId} from workspace ${ws.id} before adding to ${safeWorkspaceId}:`,
                      error
                    );
                  }
                )
              )
            );
          }
        }
      }
    }

    // Get current workspace data
    const { data: workspace, error: getError } = await admin
      .from("workspaces")
      .select("workspace_threads, metadata")
      .eq("id", safeWorkspaceId)
      .single();

    if (getError) throw getError;

    // Initialize arrays if they don't exist
    const currentThreads = workspace.workspace_threads || [];

    // Check if thread is already in the workspace
    if (currentThreads.includes(safeThreadId)) {
      return { id: safeWorkspaceId, workspace_threads: currentThreads };
    }

    // Add thread to the array
    const updatedThreads = [...currentThreads, safeThreadId];

    // Update metadata stats
    const updatedMetadata = {
      ...(workspace.metadata || {}),
      stats: {
        ...(workspace.metadata?.stats || {}),
        thread_count: updatedThreads.length,
        last_thread_added: new Date().toISOString(),
      },
    };

    // Update workspace with new thread
    const { data: updatedWorkspace, error: updateError } = await admin
      .from("workspaces")
      .update({
        workspace_threads: updatedThreads,
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", safeWorkspaceId)
      .select()
      .single();

    if (updateError) throw updateError;

    // Also update the thread to link it to this workspace
    const { error: threadUpdateError } = await admin
      .from("threads")
      .update({
        workspace_id: safeWorkspaceId,
        metadata: {
          ...(workspace.metadata || {}),
          workspace_id: safeWorkspaceId,
          is_shared_thread: true,
          shared_at: new Date().toISOString(),
        },
      })
      .eq("id", safeThreadId);

    if (threadUpdateError) {
      console.error(
        "Error updating thread with workspace info:",
        threadUpdateError
      );
      // Continue despite error - the thread is already in the workspace
    }

    return updatedWorkspace;
  } catch (error) {
    console.error("Error adding thread to workspace:", error);
    throw error;
  }
}

/**
 * Removes a thread from a workspace's thread list
 * @param {string} workspaceId - The workspace ID
 * @param {string} threadId - The thread ID to remove
 * @returns {Promise} - The updated workspace
 */
export const removeThreadFromWorkspace = async (workspaceId, threadId) => {
  try {
    // Get current workspace threads
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("workspace_threads, metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Create a new array without the threadId
    const updatedThreads = (workspace.workspace_threads || []).filter(
      (id) => id !== threadId
    );

    // Update chat count in metadata
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        chat_count: updatedThreads.length,
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace with new threads array
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        workspace_threads: updatedThreads,
        metadata: updatedMetadata,
      })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error removing thread from workspace:", error);
    throw error;
  }
};

/**
 * Gets all threads associated with a workspace
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise} - Array of thread IDs
 */
export const getWorkspaceThreads = async (workspaceId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("workspace_threads")
      .eq("id", workspaceId)
      .single();

    if (error) throw error;
    return data.workspace_threads || [];
  } catch (error) {
    console.error("Error getting workspace threads:", error);
    throw error;
  }
};

/**
 * Adds an assistant ID to a workspace's assistants array
 * @param {string} workspaceId - The workspace ID
 * @param {string} assistantId - The assistant ID to add
 * @returns {Promise} - The updated workspace
 */
export const addAssistantToWorkspace = async (workspaceId, assistantId) => {
  try {
    // Get current workspace assistants
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("workspace_assistants, metadata")
      .eq("id", workspaceId)
      .single();

    if (getError) throw getError;

    // Create a new array with the assistantId added
    const updatedAssistants = [...(workspace.workspace_assistants || [])];
    if (!updatedAssistants.includes(assistantId)) {
      updatedAssistants.push(assistantId);
    }

    // Update assistant count in metadata
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        assistant_count: updatedAssistants.length,
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace with new assistants array
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        workspace_assistants: updatedAssistants,
        metadata: updatedMetadata,
      })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error adding assistant to workspace:", error);
    throw error;
  }
};

/**
 * Removes an assistant ID from a workspace's assistants array
 * @param {string} workspaceId - The workspace ID
 * @param {string} assistantId - The assistant ID to remove
 * @returns {Promise} - The updated workspace
 */
export const removeAssistantFromWorkspace = async (
  workspaceId,
  assistantId
) => {
  try {
    // Convert IDs to strings to ensure proper comparison
    const workspaceIdStr = String(workspaceId);
    const assistantIdStr = String(assistantId);

    // Get current workspace assistants
    const { data: workspace, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("workspace_assistants, metadata")
      .eq("id", workspaceIdStr)
      .single();

    if (getError) throw getError;

    // Create a new array without the assistantId
    const updatedAssistants = (workspace.workspace_assistants || []).filter(
      (id) => String(id) !== assistantIdStr
    );

    // Add detailed logging for debugging

    // Update assistant count in metadata
    const updatedMetadata = {
      ...workspace.metadata,
      stats: {
        ...workspace.metadata.stats,
        assistant_count: updatedAssistants.length,
      },
      last_activity: new Date().toISOString(),
    };

    // Update workspace with new assistants array
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        workspace_assistants: updatedAssistants,
        metadata: updatedMetadata,
      })
      .eq("id", workspaceIdStr)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error removing assistant from workspace:", error);
    throw error;
  }
};

/**
 * Gets all assistants associated with a workspace
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise} - Array of assistant IDs
 */
export const getWorkspaceAssistants = async (workspaceId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("workspace_assistants")
      .eq("id", workspaceId)
      .single();

    if (error) throw error;
    return data.workspace_assistants || [];
  } catch (error) {
    console.error("Error getting workspace assistants:", error);
    throw error;
  }
};

/**
 * Updates a specific field of a workspace
 * @param {string} workspaceId - The workspace ID
 * @param {string} fieldName - The field name to update
 * @param {any} fieldValue - The new value for the field
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceField = async (
  workspaceId,
  fieldName,
  fieldValue
) => {
  try {
    if (!fieldName) {
      throw new Error("Field name is required for updating workspace field");
    }

    // Convert IDs to strings to ensure consistency
    const workspaceIdStr = String(workspaceId);

    // Create the update object with the single field
    const updateData = {
      [fieldName]: fieldValue,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(updateData)
      .eq("id", workspaceIdStr)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error(`Error updating workspace field ${fieldName}:`, error);
    throw error;
  }
};

/**
 * Get all shared threads for a workspace
 *
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise<Array>} - Array of thread objects
 */
export async function getWorkspaceSharedThreads(workspaceId) {
  try {
    if (!workspaceId) {
      throw new Error("Workspace ID is required");
    }

    // Ensure ID is properly formatted
    const safeWorkspaceId = String(workspaceId).replace(/[^\w-]/g, "");

    // First get the workspace to get the thread IDs
    const { data: workspace, error: workspaceError } = await supabase
      .from("workspaces")
      .select("workspace_threads")
      .eq("id", safeWorkspaceId)
      .single();

    if (workspaceError) throw workspaceError;

    if (
      !workspace.workspace_threads ||
      workspace.workspace_threads.length === 0
    ) {
      return []; // No threads in this workspace
    }

    // Fetch all thread details
    const { data: threadsData, error: threadsError } = await supabase
      .from("threads")
      .select("*")
      .in("id", workspace.workspace_threads)
      .order("updated_at", { ascending: false });

    if (threadsError) throw threadsError;

    return threadsData || [];
  } catch (error) {
    console.error("Error getting workspace shared threads:", error);
    return [];
  }
}

/**
 * Update workspace display order
 * @param {string} workspaceId - The workspace ID
 * @param {number} displayOrder - The new display order
 * @returns {Promise} - The updated workspace
 */
export const updateWorkspaceOrder = async (workspaceId, displayOrder) => {
  try {
    const { data, error } = await supabase
      .from("workspaces")
      .update({ display_order: displayOrder })
      .eq("id", workspaceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating workspace order:", error);
    throw error;
  }
};

/**
 * Update multiple workspace display orders in batch
 * @param {Array<{id: string, display_order: number}>} updates - Array of workspace updates
 * @returns {Promise} - The updated workspaces
 */
export const updateWorkspaceOrders = async (updates) => {
  try {
    // Use a transaction-like approach with Promise.all
    const updatePromises = updates.map(({ id, display_order }) =>
      supabase
        .from("workspaces")
        .update({ display_order })
        .eq("id", id)
        .select()
        .single()
    );

    const results = await Promise.all(updatePromises);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} workspace(s)`);
    }

    return results.map((r) => r.data);
  } catch (error) {
    console.error("Error updating workspace orders:", error);
    throw error;
  }
};

/**
 * Update file display order within a workspace
 * @param {string} fileId - The file ID
 * @param {number} displayOrder - The new display order
 * @returns {Promise} - The updated file
 */
export const updateFileOrder = async (fileId, displayOrder) => {
  try {
    const { data, error } = await supabase
      .from("workspace_files")
      .update({ display_order: displayOrder })
      .eq("id", fileId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating file order:", error);
    throw error;
  }
};

/**
 * Update multiple file display orders in batch
 * @param {Array<{id: string, display_order: number}>} updates - Array of file updates
 * @returns {Promise} - The updated files
 */
export const updateFileOrders = async (updates) => {
  try {
    const updatePromises = updates.map(({ id, display_order }) =>
      supabase
        .from("workspace_files")
        .update({ display_order })
        .eq("id", id)
        .select()
        .single()
    );

    const results = await Promise.all(updatePromises);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} file(s)`);
    }

    return results.map((r) => r.data);
  } catch (error) {
    console.error("Error updating file orders:", error);
    throw error;
  }
};

export const SupabaseWorkspaceUtils = {
  createWorkspace,
  getWorkspace,
  getUserWorkspaces,
  updateWorkspace,
  deleteWorkspace,
  updateWorkspaceSettings,
  updateWorkspaceMetadata,
  getHomeWorkspace,
  updateWorkspaceFileData,
  updateWorkspaceVectorData,
  updateWorkspaceStats,
  incrementWorkspaceStat,
  updateWorkspaceStorage,
  updateWorkspaceActivity,
  updateWorkspaceAppearance,
  addThreadToWorkspace,
  removeThreadFromWorkspace,
  getWorkspaceThreads,
  addAssistantToWorkspace,
  removeAssistantFromWorkspace,
  getWorkspaceAssistants,
  updateWorkspaceField,
  getWorkspaceSharedThreads,
  updateWorkspaceOrder,
  updateWorkspaceOrders,
  updateFileOrder,
  updateFileOrders,
};
